import {
  Button,
  Divider,
  Drawer,
  Popconfirm,
  Space,
  Table,
  TableProps,
  Tag,
  Typography,
  message,
} from 'antd'
import dayjs from 'dayjs'
import { memo, useCallback, useEffect, useState } from 'react'

import {
  deleteTeacher,
  getAllTeacher,
  updateTeacherDuty,
} from '~/services/teacher'

import { ITeacher } from '../../types/teacher'
import ModalForm from './ModalForm'
import { Container } from './style'

const Index = () => {
  const [loading, setLoading] = useState(false)
  const [updatingDuty, setUpdatingDuty] = useState(false)
  const [dataSource, setDataSource] = useState<ITeacher[]>([])
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerFormData, setDrawerFormData] = useState<Partial<ITeacher>>({})
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(10)

  const fetchData = useCallback(() => {
    setLoading(true)
    getAllTeacher(page, size)
      .then((res) => {
        setDataSource(res.data?.content || [])
        setTotal(res.data?.total || 0)
      })
      .catch((err) => {
        console.log(err)
        message.error(err?.message || '请求错误，请稍后再试')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [page, size])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onSearch = useCallback(() => {
    fetchData()
  }, [fetchData])

  const renderOnboardingStatus = (status?: ITeacher['onboardingStatus']) => {
    const statusMap = {
      FIRST_REVIEW_PENDING: { color: 'gold', text: '第一轮待审核' },
      FIRST_REVIEW_REJECTED: { color: 'red', text: '第一轮未通过' },
      DEMO_REQUIRED: { color: 'blue', text: '第二轮待审核' },
      DEMO_REJECTED: { color: 'red', text: '第二轮未通过' },
      TRAINING_REQUIRED: { color: 'orange', text: '待完成上岗任务' },
      ACTIVE: { color: 'green', text: '具备上课资格' },
    } as const
    const item = statusMap[status ?? 'ACTIVE'] ?? {
      color: 'default',
      text: status || '未知',
    }
    return <Tag color={item.color}>{item.text}</Tag>
  }

  const columns: TableProps<ITeacher>['columns'] = [
    {
      title: '教师姓名',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      fixed: 'left',
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: '头像',
      dataIndex: 'profileImgUrl',
      key: 'profileImgUrl',
      width: 80,
      render: (url) => (
        <img
          style={{
            width: 40,
            height: 40,
            objectFit: 'cover',
            borderRadius: 40,
          }}
          src={url}
        />
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 220,
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
    },
    {
      title: '国籍',
      dataIndex: 'nationality',
      key: 'nationality',
      width: 100,
    },
    {
      title: '资质',
      dataIndex: 'qualification',
      key: 'qualification',
      width: 200,
    },
    {
      title: '每课薪资',
      dataIndex: 'rate',
      key: 'rate',
      width: 120,
      align: 'center',
      render: (value: number, record: ITeacher) => {
        const currencySymbol: Record<string, string> = {
          CNY: '¥',
          USD: '$',
          EUR: '€',
          GBP: '£',
          JPY: '¥',
          KRW: '₩',
          PHP: '₱',
          THB: '฿',
        }
        const symbol = currencySymbol[record.currency] || ''
        return `${symbol}${value ?? '-'}`
      },
    },
    {
      title: '货币类型',
      dataIndex: 'currency',
      key: 'currency',
      width: 100,
      align: 'center',
    },
    {
      title: '课时卡/节',
      dataIndex: 'cardsPerLesson',
      key: 'cardsPerLesson',
      width: 120,
      align: 'center',
    },
    {
      title: '介绍',
      dataIndex: 'introduction',
      key: 'introduction',
      render: (text) => `${text.slice(0, 50)}...`,
    },
    {
      title: '上岗状态',
      dataIndex: 'onDuty',
      key: 'onDuty',
      width: 100,
      render: (onDuty?: boolean) => (
        <Tag color={onDuty === true ? 'green' : 'default'}>
          {onDuty === true ? '在岗' : onDuty === false ? '下岗' : '未知'}
        </Tag>
      ),
    },
    {
      title: '账号状态',
      dataIndex: 'onboardingStatus',
      key: 'onboardingStatus',
      width: 150,
      render: renderOnboardingStatus,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
      width: 180,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
      width: 180,
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      render: (text, record) => {
        return (
          <Space>
            <Typography.Link
              onClick={() => {
                setDrawerFormData(record)
                setIsDrawerOpen(true)
              }}
            >
              编辑
            </Typography.Link>
            <Divider style={{ marginInline: 0 }} type="vertical" />
            <Popconfirm
              title={record.onDuty ? '确认下岗？' : '确认上岗？'}
              description={
                record.onDuty
                  ? '下岗后将从学生选老师、收藏和预约入口隐藏，暂停新预约及固定课自动续约。已有预约继续保留。'
                  : '上岗后，具备上课资格的老师将重新对学生可见，并恢复接受新预约及固定课自动续约。'
              }
              disabled={typeof record.onDuty !== 'boolean' || updatingDuty}
              onConfirm={async () => {
                if (typeof record.onDuty !== 'boolean' || updatingDuty) return
                const onDuty = !record.onDuty
                setUpdatingDuty(true)
                try {
                  await updateTeacherDuty(record.id, onDuty)
                  setDataSource((teachers) =>
                    teachers.map((teacher) =>
                      teacher.id === record.id
                        ? { ...teacher, onDuty }
                        : teacher,
                    ),
                  )
                  message.success(onDuty ? '已上岗' : '已下岗')
                  fetchData()
                } catch (err) {
                  message.error(
                    err instanceof Error
                      ? err.message
                      : '状态更新失败，请稍后再试',
                  )
                } finally {
                  setUpdatingDuty(false)
                }
              }}
              okText={record.onDuty ? '下岗' : '上岗'}
              cancelText="取消"
            >
              <Typography.Link
                disabled={typeof record.onDuty !== 'boolean' || updatingDuty}
              >
                {record.onDuty === false ? '上岗' : '下岗'}
              </Typography.Link>
            </Popconfirm>
            <Divider style={{ marginInline: 0 }} type="vertical" />
            <Popconfirm
              title="提示"
              description="确认删除？"
              onConfirm={() => {
                deleteTeacher(record.id)
                  .then(() => {
                    message.success('删除成功')
                    fetchData()
                  })
                  .catch((err) => {
                    message.error(err?.message || '删除失败，请稍后再试～')
                  })
              }}
              okText="删除"
              cancelText="取消"
            >
              <Typography.Link type="danger">删除</Typography.Link>
            </Popconfirm>
          </Space>
        )
      },
      fixed: 'right',
      width: 220,
      align: 'center',
    },
  ]

  const addData = useCallback(() => {
    setIsDrawerOpen(true)
  }, [])
  const closeDrawer = useCallback(() => {
    setDrawerFormData({})
    setIsDrawerOpen(false)
  }, [])
  const onPageChange = useCallback((cPage: number, cSize: number) => {
    setPage(cPage)
    setSize(cSize)
  }, [])

  return (
    <Container>
      <div className="top">
        <Button
          style={{ marginRight: 12 }}
          type="primary"
          loading={loading}
          onClick={onSearch}
        >
          查询
        </Button>
        <Button onClick={addData}>新增</Button>
      </div>
      <div className="table">
        <Table
          rowKey="id"
          scroll={{ x: 2300, y: 510 }}
          loading={loading}
          columns={columns}
          dataSource={dataSource}
          pagination={{
            total,
            pageSize: size,
            current: page,
            onChange: onPageChange,
          }}
        />
      </div>
      <div className="pagination"></div>
      <Drawer
        title={drawerFormData.id ? '编辑教师' : '新增教师'}
        open={isDrawerOpen}
        onClose={closeDrawer}
        width={450}
        destroyOnClose
      >
        <ModalForm data={drawerFormData} refreshTable={fetchData} />
      </Drawer>
    </Container>
  )
}

export default memo(Index)
